/**
 * Superficie pública del módulo STOCK (inventario físico, consultas, ajustes, transferencias, CSV).
 * Fuera de `modules/stock/**` importar solo desde este archivo (ver `check-stock-module-boundaries.mjs`).
 */

/** Dominio: tipos y razones */
export type {
  BranchStockActivityRow,
  GetStockMovementHistoryOptions,
  InventoryDashboardRow,
  ProductBranchStockRow,
  StockMovementHistoryFilters,
  StockMovementRow,
  StockRow,
  StockSort,
  StockStatusFilter,
  TransferRow,
} from './domain/stockTypes';

export {
  STOCK_MOVEMENT_REASONS,
  getStockMovementReasonLabel,
  extractSaleIdFromStockMovement,
  extractWorkOrderIdFromStockMovement,
  isSaleStockMovementReason,
} from './domain/stockReasons';
export type { StockMovementReasonKey } from './domain/stockReasons';

export {
  selectStockIdsForMovementHistory,
  type StockScopeRow,
} from './domain/stockHistoryScope';

/** Infra: consultas */
export {
  countStockByBranch,
  getInventoryDashboard,
  getLastStockMovementUnitCost,
  getStockByBranch,
  getStockByProductAndBranch,
  isUnstockedStockRow,
  listVariantStockCells,
  type GetStockByBranchOptions,
  type VariantStockCellRow,
} from './infra/queries/stockQueries';

export {
  fetchProductsWithoutSales,
  STOCK_NO_SALES_DAYS_DEFAULT,
  type ProductsWithoutSalesResult,
} from './infra/queries/productsWithoutSales';

/** Infra: ajustes y reposición */
export {
  adjustStock,
  applyPhysicalCountMixedBatch,
  bulkUpdateQuantitiesByCoefficient,
  receiveStock,
} from './infra/operations/stockAdjustmentsWrite';
export type {
  PhysicalCountMixedItem,
} from './infra/operations/stockAdjustmentsWrite';

/** Infra: transferencias */
export {
  getPendingTransfersToReceive,
  receiveTransfer,
  rejectTransfer,
  transferStock,
} from './infra/operations/stockTransfers';

/** Infra: historial de movimientos */
export {
  getStockMovementHistory,
  getStockMovementHistoryForExport,
} from './infra/history/stockMovementHistory';

/** Infra: guards inventario */
export {
  assertProductIsInventoriable,
  assertVariantBelongsToProduct,
  isInventoriableProduct,
  resolveVariantLabel,
} from './infra/guards/stockInventoryGuards';

/** Infra: helpers para transacciones de stock (BUG-17) */
export {
  findStockInWebCache,
} from './infra/operations/stockAdjustmentsRead';

/** Infra: split vendible/vencido de lotes FEFO (lectura RAM, sync). */
export {
  getStockLotExpirySplit,
  type StockLotExpiryRow,
  type StockLotExpirySplit,
} from './infra/guards/fefoAllocator';

/** Infra: actividad sucursal */
export { getBranchStockActivity, BRANCH_ACTIVITY_FETCH_LIMIT } from './infra/queries/stockActivity';

/** Infra: CSV historial */
export { buildStockMovementHistoryCsv } from './infra/history/stockMovementHistoryCsv';

/** CSV import/export de stock por sucursal */
export type { StockCsvPreviewRow, StockCsvPreviewResult, StockCsvCommitResult } from './infra/csv/stockCsvImport';
export {
  buildStockCsvExport,
  buildStockCsvTemplate,
} from './infra/csv/stockCsvExport';
export { buildStockListCsv } from './infra/csv/stockListCsvExport';
export {
  previewStockCsvImport,
  commitStockCsvImport,
  buildStockImportErrorsCsv,
} from './infra/csv/stockCsvImport';
export {
  stockCsvTemplateFilename,
  stockCsvExportFilename,
  STOCK_CSV_IMPORT_HEADERS,
  STOCK_CSV_EXPORT_HEADERS,
  STOCK_CSV_MOTIVO_LABELS,
  STOCK_CSV_REQUIRED_IMPORT_HEADERS,
  looksLikeStockSnapshotExport,
} from './domain/csv/stockCsvSchemas';
export { parseStockCsvRowValues } from './domain/csv/stockCsvRowParse';

/** Errores de dominio */
export {
  NonInventoriableProductError,
  StockDomainError,
  StockInsufficientError,
  StockProductNotFoundError,
  StockReceiveValidationError,
  StockTransferNotFoundError,
  StockTransferValidationError,
} from './domain/stockErrors';

export {
  ensureVariantStockRow,
  findStockRow,
  findStockRowsForVariant,
  maintainParentStockSum,
} from './infra/operations/stockRowLookup';
